import cloneDeep from 'clone-deep'
import { useMemo, useState } from 'react'

import { IMcrAdminView, IMcrConfig } from './types'

/**
 * General ledger account to Financial Condition line.
 *
 * The report classifies an unmapped account from its type and name, and prints which lines it
 * inferred, so this table exists to correct the guesses rather than to enter two hundred rows. The
 * inferred value sits beside the selector for that reason, and the filter defaults to showing
 * everything so nothing is hidden from someone auditing the mapping.
 */
export const McrFcAccounts = ({
  view,
  config,
  onChange,
}: {
  view: IMcrAdminView
  config: IMcrConfig
  onChange: (next: IMcrConfig) => void
}) => {
  const [query, setQuery] = useState('')
  const [onlyInferred, setOnlyInferred] = useState(false)

  const set = (accountNumber: string, value: string) => {
    const next = cloneDeep(config)
    if (!value) delete next.accountLines[accountNumber]
    else next.accountLines[accountNumber] = value
    onChange(next)
  }

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    return view.accounts.filter((account) => {
      if (onlyInferred && config.accountLines[account.number]) return false
      if (!q) return true
      return (
        String(account.number).toLowerCase().includes(q) ||
        String(account.name).toLowerCase().includes(q) ||
        String(account.type).toLowerCase().includes(q)
      )
    })
  }, [view.accounts, config.accountLines, query, onlyInferred])

  if (!view.accounts.length)
    return (
      <p className="text-sm text-gray-500">
        No general ledger accounts exist yet. Set up the chart of accounts under Accounting, and the
        Financial Condition will build itself from it.
      </p>
    )

  return (
    <div>
      <p className="mb-3 text-[12px] text-gray-500">
        An account left on <span className="italic">Inherit</span> is classified from its type and name.
        The Financial Condition report prints an Account Mapping page marking every inferred line, so
        check that page and correct only what the inference got wrong. Choosing{' '}
        <span className="italic">Not Reported</span> holds an account off the statement entirely.
      </p>

      <div className="mb-3 flex items-center gap-3">
        <input
          type="text"
          className="w-64 rounded border border-gray-300 px-2 py-1 text-sm"
          placeholder={`Search ${view.accounts.length} accounts…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={onlyInferred} onChange={(e) => setOnlyInferred(e.target.checked)} />
          Only accounts still inheriting
        </label>
      </div>

      <table className="w-full text-sm">
        <thead className="uppercase text-xs bg-gray-50">
          <tr>
            {['No', 'Account', 'Type', 'FC Line', 'Resolves To'].map((v) => (
              <th scope="col" className="p-3 border" key={`header-${v}`}>
                {v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((account, index) => {
            const explicit = config.accountLines[account.number] || ''
            const effective = explicit || account.inferredLine
            return (
              <tr key={account.no}>
                <td className="p-2 border w-12 text-center">{index + 1}</td>
                <td className="px-2 border">
                  {account.number ? `${account.number} - ` : ''}
                  {account.name}
                </td>
                <td className="px-2 border text-[12px] text-gray-500">{account.type}</td>
                <td className="px-2 py-1 border w-72">
                  <select
                    className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200"
                    value={explicit}
                    onChange={(e) => set(account.number, e.target.value)}
                  >
                    <option value="">- Inherit -</option>
                    {Object.keys(view.fcLineOptions).map((optKey) => (
                      <option key={optKey} value={optKey}>
                        {view.fcLineOptions[optKey]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 border text-[12px] text-gray-500">
                  {view.fcLineOptions[effective] || '-'}
                  {!explicit && <span className="ml-1 text-gray-400">(inferred)</span>}
                </td>
              </tr>
            )
          })}
          {!rows.length && (
            <tr>
              <td className="p-3 border text-[12px] text-gray-400" colSpan={5}>
                No accounts match.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
